import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Globe,
  Loader2,
  Video,
} from "lucide-react";
import {
  HOST_TIMEZONE,
  formatSlotLong,
  slotsForDay,
  upcomingWeekdays,
  visitorTimezone,
} from "../../utils/demoSlots";
import { bookDemo, getBookedDemoSlots } from "../../services/collavueApi";

const DAY_COUNT = 15;

const PERKS = [
  "A 20-minute walkthrough of live lead discovery on your own market",
  "We build one campaign with you, on your site and your ICP",
  "Straight answers on pricing, limits and what Collavue will not do",
];

/**
 * Demo booking. Picking a slot writes the booking server-side and emails the
 * Collavue team the date, time and reason — there is no calendar integration
 * to connect.
 */
export default function BookDemo({ id = "book-demo" }) {
  const days = useMemo(() => upcomingWeekdays(DAY_COUNT), []);
  // Land on the first day that still has slots. Opening on "today" after
  // close of business shows a grid of struck-through times and reads as
  // "fully booked".
  const [dayKey, setDayKey] = useState(() => {
    const open = days.find((d) =>
      slotsForDay(d.key).some((slot) => !slot.past)
    );
    return (open || days[0])?.key || "";
  });
  const [taken, setTaken] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotIso, setSlotIso] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [booked, setBooked] = useState(null);

  const tz = useMemo(() => visitorTimezone(), []);
  const stripRef = useRef(null);
  const detailsRef = useRef(null);

  // Refresh claimed slots whenever the day changes so a taken time greys out
  // before someone fills in the whole form.
  useEffect(() => {
    if (!dayKey) return;
    let cancelled = false;
    setLoadingSlots(true);
    getBookedDemoSlots(dayKey)
      .then((r) => {
        if (!cancelled) setTaken(Array.isArray(r?.taken) ? r.taken : []);
      })
      .catch(() => {
        if (!cancelled) setTaken([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dayKey]);

  const slots = useMemo(
    () => slotsForDay(dayKey, { taken }),
    [dayKey, taken]
  );
  const selected = slots.find((s) => s.iso === slotIso) || null;
  const openCount = slots.filter((s) => !s.past && !s.taken).length;

  const scrollStrip = useCallback((dir) => {
    const el = stripRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(160, el.clientWidth * 0.7), behavior: "smooth" });
  }, []);

  const chooseSlot = (slot) => {
    setSlotIso(slot.iso);
    setError("");
    // On mobile the form sits below the fold — bring it into view.
    window.requestAnimationFrame(() => {
      detailsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");

    if (!selected) return setError("Pick a time slot first.");
    if (!form.name.trim()) return setError("Please add your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) {
      return setError("Please add a valid work email.");
    }
    if (form.reason.trim().length < 3) {
      return setError("Tell us what you'd like to cover on the call.");
    }

    setSubmitting(true);
    try {
      const res = await bookDemo({
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        reason: form.reason.trim(),
        startsAt: selected.iso,
        timezone: tz,
        source: "landing",
      });
      setBooked({
        at: selected.at,
        when:
          res?.visitorWhen ||
          formatSlotLong(selected.at, tz) ||
          res?.hostWhen ||
          "",
        email: form.email.trim(),
      });
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
      // The slot was claimed between load and submit — refresh the grid.
      if (err?.code === "SLOT_TAKEN") {
        setSlotIso("");
        getBookedDemoSlots(dayKey)
          .then((r) => setTaken(Array.isArray(r?.taken) ? r.taken : []))
          .catch(() => {});
      }
    } finally {
      setSubmitting(false);
    }
  };

  const field =
    "w-full rounded-lg border border-[#e3dccb] bg-white px-3.5 py-2.5 text-[15px] text-[#0a1736] placeholder:text-[#9aa3b5] focus:outline-none focus:border-[#2d5bff] focus:ring-2 focus:ring-[#2d5bff]/15 transition-colors";

  return (
    <section id={id} className="scroll-mt-24 bg-[#f3ecdd]/60 border-y border-[#e3dccb]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Pitch */}
          <div className="lg:col-span-5 min-w-0">
            <p className="text-[#2d5bff] text-xs sm:text-sm uppercase tracking-wider font-medium">
              Book a demo
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] text-[#0a1736] font-semibold mt-3 leading-[1.1]">
              See Collavue find real buyers{" "}
              <span className="italic text-[#2d5bff]">in your market.</span>
            </h2>
            <p className="mt-4 text-[#5a6478] text-base sm:text-lg leading-relaxed">
              Pick a time that works. We'll run a live search against your
              market on the call — no slideware, no scripted demo data.
            </p>

            <ul className="mt-8 space-y-3">
              {PERKS.map((perk) => (
                <li key={perk} className="flex items-start gap-3">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[#2d5bff]/10 text-[#2d5bff] flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  <span className="text-[15px] text-[#1c2a4d] leading-relaxed">
                    {perk}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#5a6478]">
              <span className="inline-flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#2d5bff]" /> 20 minutes
              </span>
              <span className="inline-flex items-center gap-2">
                <Video className="w-4 h-4 text-[#2d5bff]" /> Google Meet
              </span>
              <span className="inline-flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#2d5bff]" />
                {tz === HOST_TIMEZONE ? "Eastern Time" : `Times in ${tz}`}
              </span>
            </div>
          </div>

          {/* Picker */}
          <div className="lg:col-span-7 w-full min-w-0">
            <div className="bg-white rounded-2xl border border-[#e3dccb] shadow-[0_24px_70px_-40px_rgba(10,23,54,0.45)] overflow-hidden min-w-0">
              <AnimatePresence mode="wait" initial={false}>
                {booked ? (
                  <motion.div
                    key="confirmed"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-6 sm:p-10 text-center"
                  >
                    <span className="w-12 h-12 rounded-full bg-[#2d5bff]/10 text-[#2d5bff] flex items-center justify-center mx-auto">
                      <CheckCircle2 className="w-6 h-6" />
                    </span>
                    <h3 className="font-display text-2xl sm:text-3xl text-[#0a1736] font-semibold mt-5">
                      You're booked.
                    </h3>
                    <p className="mt-3 text-[#5a6478] text-base leading-relaxed max-w-md mx-auto">
                      We've got you down for{" "}
                      <span className="text-[#0a1736] font-medium">
                        {booked.when}
                      </span>
                      . The team has been notified and will send the meeting
                      link to{" "}
                      <span className="text-[#0a1736] font-medium break-all">
                        {booked.email}
                      </span>
                      .
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setBooked(null);
                        setSlotIso("");
                        setForm({ name: "", email: "", company: "", reason: "" });
                      }}
                      className="mt-7 text-sm text-[#2d5bff] underline underline-offset-4 hover:text-[#0a1736] transition-colors"
                    >
                      Book another time
                    </button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="picker"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-4 sm:p-6 lg:p-8"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <CalendarDays className="w-4 h-4 text-[#2d5bff] shrink-0" />
                        <span className="text-sm font-medium text-[#0a1736] truncate">
                          Choose a day
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => scrollStrip(-1)}
                          aria-label="Earlier days"
                          className="p-1.5 rounded-md border border-[#e3dccb] text-[#1c2a4d] hover:border-[#0a1736] transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => scrollStrip(1)}
                          aria-label="Later days"
                          className="p-1.5 rounded-md border border-[#e3dccb] text-[#1c2a4d] hover:border-[#0a1736] transition-colors"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div
                      ref={stripRef}
                      className="mt-3 flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                    >
                      {days.map((d) => {
                        const active = d.key === dayKey;
                        return (
                          <button
                            key={d.key}
                            type="button"
                            aria-pressed={active}
                            onClick={() => {
                              setDayKey(d.key);
                              setSlotIso("");
                            }}
                            className={`snap-start shrink-0 w-[68px] sm:w-[76px] rounded-xl border px-2 py-2.5 text-center transition-colors ${
                              active
                                ? "border-[#2d5bff] bg-[#2d5bff] text-white"
                                : "border-[#e3dccb] bg-white text-[#0a1736] hover:border-[#0a1736]"
                            }`}
                          >
                            <span
                              className={`block text-[11px] uppercase tracking-wide ${
                                active ? "text-white/75" : "text-[#5a6478]"
                              }`}
                            >
                              {d.isTommorrow ? "Tmrw" : d.weekday}
                            </span>
                            <span className="block text-lg font-semibold leading-6">
                              {d.day}
                            </span>
                            <span
                              className={`block text-[11px] ${
                                active ? "text-white/75" : "text-[#5a6478]"
                              }`}
                            >
                              {d.month}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="mt-5 flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-[#0a1736]">
                        Pick a time
                      </span>
                      <span className="text-xs text-[#5a6478] inline-flex items-center gap-1.5">
                        {loadingSlots ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" /> Checking
                            availability
                          </>
                        ) : (
                          `${openCount} open`
                        )}
                      </span>
                    </div>

                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2">
                      {slots.map((s) => {
                        const disabled = s.past || s.taken;
                        const active = s.iso === slotIso;
                        return (
                          <button
                            key={s.iso}
                            type="button"
                            disabled={disabled}
                            aria-pressed={active}
                            onClick={() => chooseSlot(s)}
                            className={`rounded-lg border px-2 py-2.5 text-sm font-medium transition-colors ${
                              disabled
                                ? "border-[#ece5d6] bg-[#faf6ee] text-[#b3b9c6] line-through cursor-not-allowed"
                                : active
                                ? "border-[#2d5bff] bg-[#2d5bff] text-white"
                                : "border-[#e3dccb] bg-white text-[#0a1736] hover:border-[#2d5bff] hover:text-[#2d5bff]"
                            }`}
                          >
                            <span className="block">{s.localLabel || s.label}</span>
                            {s.localLabel && (
                              <span
                                className={`block text-[11px] font-normal ${
                                  active ? "text-white/75" : "text-[#5a6478]"
                                }`}
                              >
                                {s.label} ET
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {openCount === 0 && !loadingSlots && (
                      <p className="mt-3 text-sm text-[#5a6478]">
                        Nothing left on this day — try the next one.
                      </p>
                    )}

                    <div ref={detailsRef} className="scroll-mt-24">
                      <AnimatePresence initial={false}>
                        {selected && (
                          <motion.form
                            key="details"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25 }}
                            onSubmit={onSubmit}
                            className="overflow-hidden"
                          >
                            <div className="mt-6 pt-6 border-t border-[#e3dccb]">
                              <p className="text-sm text-[#5a6478]">
                                Booking{" "}
                                <span className="text-[#0a1736] font-medium">
                                  {formatSlotLong(selected.at, tz)}
                                </span>
                              </p>

                              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                                <div>
                                  <label
                                    htmlFor="demo-name"
                                    className="block text-xs font-medium text-[#1c2a4d] mb-1.5"
                                  >
                                    Name
                                  </label>
                                  <input
                                    id="demo-name"
                                    name="name"
                                    autoComplete="name"
                                    value={form.name}
                                    onChange={(e) =>
                                      setForm((f) => ({ ...f, name: e.target.value }))
                                    }
                                    placeholder="Alex Rivera"
                                    className={field}
                                  />
                                </div>
                                <div>
                                  <label
                                    htmlFor="demo-email"
                                    className="block text-xs font-medium text-[#1c2a4d] mb-1.5"
                                  >
                                    Work email
                                  </label>
                                  <input
                                    id="demo-email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    inputMode="email"
                                    value={form.email}
                                    onChange={(e) =>
                                      setForm((f) => ({ ...f, email: e.target.value }))
                                    }
                                    placeholder="alex@company.com"
                                    className={field}
                                  />
                                </div>
                              </div>

                              <div className="mt-3">
                                <label
                                  htmlFor="demo-company"
                                  className="block text-xs font-medium text-[#1c2a4d] mb-1.5"
                                >
                                  Company{" "}
                                  <span className="text-[#9aa3b5] font-normal">
                                    (optional)
                                  </span>
                                </label>
                                <input
                                  id="demo-company"
                                  name="company"
                                  autoComplete="organization"
                                  value={form.company}
                                  onChange={(e) =>
                                    setForm((f) => ({ ...f, company: e.target.value }))
                                  }
                                  placeholder="Company name"
                                  className={field}
                                />
                              </div>

                              <div className="mt-3">
                                <label
                                  htmlFor="demo-reason"
                                  className="block text-xs font-medium text-[#1c2a4d] mb-1.5"
                                >
                                  What do you want to cover?
                                </label>
                                <textarea
                                  id="demo-reason"
                                  name="reason"
                                  rows={3}
                                  value={form.reason}
                                  onChange={(e) =>
                                    setForm((f) => ({ ...f, reason: e.target.value }))
                                  }
                                  placeholder="We sell compliance software to fintech ops teams and want to see what Collavue surfaces for us."
                                  className={`${field} resize-y min-h-[88px]`}
                                />
                              </div>

                              {error && (
                                <p
                                  role="alert"
                                  className="mt-3 text-sm text-[#b3261e] bg-[#b3261e]/5 border border-[#b3261e]/20 rounded-lg px-3 py-2"
                                >
                                  {error}
                                </p>
                              )}

                              <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
                                <button
                                  type="submit"
                                  disabled={submitting}
                                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#0a1736] text-white font-medium hover:bg-[#1c2a4d] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  {submitting && (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  )}
                                  {submitting ? "Booking…" : "Confirm booking"}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setSlotIso("")}
                                  className="text-sm text-[#5a6478] hover:text-[#0a1736] transition-colors"
                                >
                                  Pick a different time
                                </button>
                              </div>
                            </div>
                          </motion.form>
                        )}
                      </AnimatePresence>
                    </div>

                    {!selected && error && (
                      <p role="alert" className="mt-4 text-sm text-[#b3261e]">
                        {error}
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
